import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Sparkles
} from "lucide-react";

const Quiz = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [useAI, setUseAI] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [category, useAI]);

  const fetchQuiz = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let response;
      
      if (useAI) {
        response = await axios.post(
          `${API}/quiz/generate`,
          { category, num_questions: 5 },
          { headers }
        );
      } else {
        response = await axios.get(`${API}/quiz/${category}?num_questions=5`, { headers });
      }
      
      setQuiz(response.data);
      setCurrentIndex(0);
      setAnswers([]);
      setSelectedAnswer(null);
      setStartTime(Date.now());
    } catch (error) {
      toast.error("Erreur lors du chargement du quiz");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNext = () => {
    if (selectedAnswer === null) {
      toast.error("Veuillez sélectionner une réponse");
      return;
    }
    
    const newAnswers = [...answers, selectedAnswer];
    setAnswers(newAnswers);
    
    if (currentIndex < quiz.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswer(answers[currentIndex - 1]);
      setAnswers(answers.slice(0, -1));
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const response = await axios.post(
        `${API}/quiz/submit`,
        {
          quiz_id: quiz.quiz_id,
          answers: finalAnswers,
          time_spent: timeSpent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      navigate("/quiz-result", { state: { result: response.data, category } });
    } catch (error) {
      toast.error("Erreur lors de la soumission du quiz");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="spinner"></div>
          <p className="text-slate-600">Chargement du quiz...</p>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <p className="text-slate-600">Quiz non trouvé</p>
          <Button onClick={() => navigate("/dashboard")}>
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quiz-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="text-slate-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUseAI(!useAI)}
              className={useAI ? "border-purple-500 text-purple-600" : ""}
              data-testid="toggle-ai-btn"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              {useAI ? "Mode IA" : "Mode Standard"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchQuiz}
              data-testid="refresh-quiz-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">
              Question {currentIndex + 1} sur {quiz.questions.length}
            </span>
            <span className={`category-badge ${category}`}>
              {category}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="quiz-question" data-testid="quiz-question">
              {currentQuestion.question}
            </h2>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectAnswer(index)}
                  className={`quiz-option ${selectedAnswer === index ? 'selected' : ''}`}
                  data-testid={`option-${index}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-8 w-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${
                      selectedAnswer === index 
                        ? 'border-blue-600 bg-blue-600 text-white' 
                        : 'border-slate-300 text-slate-500'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span>{option}</span>
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            data-testid="prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Précédent
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={selectedAnswer === null || submitting}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="next-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : currentIndex === quiz.questions.length - 1 ? (
              <>
                Terminer
                <CheckCircle className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                Suivant
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Quiz;
